import { motion } from 'motion/react';
import React, { useLayoutEffect, useRef, useState } from 'react';

import { Fade, Icon } from '@column/column-ui-kit';
import { Badge, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, cn } from '@column/ui';

import { type DecisionAnimationState } from '~/lib/approvals/hooks/useDecisionAnimation';
import { type SlideDirection } from '~/lib/approvals/hooks/useReviewTransition';
import { PageCounter } from '~/lib/components/PageCounter';
import { useStepUpStore } from '~/stores/StepUp';
import { Text } from '~/styles';

import { AnimatedButton } from './AnimatedButton';

export const EASE = [0.25, 0.1, 0.25, 1] as const;
export const DIALOG_WIDEN_MS = 300;
export const BACKGROUND_NOTICE = 'You may close this window, processing will continue in the background.';
const SETTLE_MS = 300;

interface AnimatedHeightProps {
  children: React.ReactNode;
  className?: string;
  freeze?: boolean;
  durationMs?: number;
}

export const AnimatedHeight: React.FC<AnimatedHeightProps> = ({
  children,
  className,
  freeze = false,
  durationMs = 200,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const freezeRef = useRef(freeze);
  freezeRef.current = freeze;
  const [height, setHeight] = useState<number | 'auto'>('auto');
  const [isSettled, setIsSettled] = useState(false);

  useLayoutEffect(() => {
    const element = contentRef.current;
    if (!element) return undefined;
    setHeight(element.offsetHeight);
    const observer = new ResizeObserver(() => {
      const next = element.offsetHeight;
      setHeight((current) => (freezeRef.current && current !== 'auto' && next < current ? current : next));
    });
    observer.observe(element);
    const settle = window.setTimeout(() => setIsSettled(true), SETTLE_MS);
    return () => {
      window.clearTimeout(settle);
      observer.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    if (!freeze && contentRef.current) setHeight(contentRef.current.offsetHeight);
  }, [freeze]);

  return (
    <motion.div
      className="-mx-6 -mt-2 box-content min-w-0 overflow-hidden px-6 py-2"
      initial={false}
      animate={{ height }}
      transition={{ duration: isSettled ? durationMs / 1000 : 0, ease: EASE }}
    >
      <div ref={contentRef} className={cn('min-w-0', className)}>
        {children}
      </div>
    </motion.div>
  );
};

interface ReviewDialogProps {
  isOpen: boolean;
  isWide?: boolean;
  onClose: () => void;
  className?: string;
  showCloseButton?: boolean;
  children: React.ReactNode;
}

export const ReviewDialog: React.FC<ReviewDialogProps> = ({
  isOpen,
  isWide = false,
  onClose,
  className,
  showCloseButton = true,
  children,
}) => {
  const isStepUpOpen = useStepUpStore((state) => state.challenge !== null);
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isStepUpOpen && onClose()}>
      <DialogContent
        showCloseButton={showCloseButton}
        className={cn('transition-[max-width]', className, isWide && 'sm:max-w-3xl')}
        style={{
          transitionDuration: `${DIALOG_WIDEN_MS}ms`,
          transitionTimingFunction: `cubic-bezier(${EASE.join(', ')})`,
        }}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement | null)?.focus();
        }}
        onInteractOutside={(event) => isStepUpOpen && event.preventDefault()}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};

interface ReviewDialogHeaderProps {
  title: string;
  description: React.ReactNode;
  isDescriptionVisible?: boolean;
  showCounter: boolean;
  slideDirection: SlideDirection;
  current: number;
  total: number;
  onPrevious: () => void;
  onNext: () => void;
}

export const ReviewDialogHeader: React.FC<ReviewDialogHeaderProps> = ({
  title,
  description,
  isDescriptionVisible = false,
  showCounter,
  slideDirection,
  current,
  total,
  onPrevious,
  onNext,
}) => (
  <DialogHeader className="relative gap-1 pb-1 text-left">
    <DialogTitle>{title}</DialogTitle>
    <DialogDescription className={cn(!isDescriptionVisible && 'sr-only')}>{description}</DialogDescription>
    <Fade
      show={showCounter}
      direction={slideDirection === 'up' ? 'right' : 'left'}
      className="absolute top-0 right-0 flex h-5 items-center"
    >
      <PageCounter current={current} total={total} onPrevious={onPrevious} onNext={onNext} />
    </Fade>
  </DialogHeader>
);

export interface ReviewItemBadge {
  icon: React.ReactNode;
  label: string;
}

export const ReviewItemHeader: React.FC<{ title: string; badges: ReviewItemBadge[]; action?: React.ReactNode }> = ({
  title,
  badges,
  action,
}) => (
  <div className="flex flex-col gap-1 px-4 pt-3 pb-2">
    <Text className="truncate" size={16} weight="medium" lineHeight="28px" letterSpacing="tightest" color="foreground">
      {title}
    </Text>
    <div className="flex min-w-0 items-center gap-3">
      <div className="-ml-2 flex min-w-0 flex-1 flex-wrap items-center gap-x-0 leading-8">
        {badges.map((badge) => (
          <Badge
            key={badge.label}
            variant="ghost"
            className="gap-1.5 text-sm font-medium text-muted-foreground [&>svg]:size-4"
          >
            {badge.icon}
            {badge.label}
          </Badge>
        ))}
      </div>
      {action && <div className="ml-auto flex shrink-0 justify-end">{action}</div>}
    </div>
  </div>
);

const getOptionKey = () => (navigator.userAgent.includes('Mac') ? '⌥' : 'Alt+');

const ShortcutHint: React.FC<{ letter: string }> = ({ letter }) => (
  <span style={{ marginLeft: 8, opacity: 0.6, fontSize: 12 }}>
    {getOptionKey()}
    {letter}
  </span>
);

interface DecisionButtonProps {
  onClick: () => void;
  isLoading: boolean;
  isDisabled: boolean;
  animationState: DecisionAnimationState;
  children: React.ReactNode;
}

export const RejectButton: React.FC<DecisionButtonProps> = ({ children, ...props }) => (
  <AnimatedButton {...props} icon={<Icon.Cross />} variant="danger">
    {children} <ShortcutHint letter="R" />
  </AnimatedButton>
);

export const ApproveButton: React.FC<DecisionButtonProps> = ({ children, ...props }) => (
  <AnimatedButton {...props} icon={<Icon.Checkmark />}>
    {children} <ShortcutHint letter="A" />
  </AnimatedButton>
);
